import { Component } from 'react';
import moment from 'moment';
import { get, isEqual } from 'lodash';
import { WithStyles, withStyles } from '@mui/styles';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	FormControlLabel,
	RadioGroup,
	Typography
} from '@mui/material';
import Cookies from 'js-cookie';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import classnames from 'classnames';

// Contexts
import { withTranslate } from './../../../core/TenantProvider';
import withFlags from '../../../core/FlagsProvider';
// Components - Atoms, Organisms,Containers
import APHeading from './../../atoms/APHeading';
import APRadio from "./../../atoms/APRadio";
import APButton from './../../atoms/APButton';
import APLabel from '../../atoms/APLabel';
import APAlertMessage from './../../atoms/APAlertMessage';
import businessSector from '../../../core/DropdownReferenceData/businessSector.json';
import businessCategory from '../../../core/DropdownReferenceData/businessCategory.json';
import salutations from '../../../core/DropdownReferenceData/salutations.json';
// Files in the same folder
import { COMPONENT_DEFINITIONS } from './definitions/components';
import { ReactComponent as AccordionIcon } from './assets/icon-chevron-up.svg';
import APStepPanelStyles from './styles/APStepPanel.style';
// Types
import { dropdownOptionType } from '../../../core/types/GeneralTypes';
// Reducer
import { EtvOptions } from '../../../redux/modules/refer/reducer';
// Utils
import { addressCountryLabelMapping } from '../../../containers/SettingsContainer/utils/getters';
import { getOrganizationName } from '../../../core/utils/OrganizationValueGetter/OrganizationValueGetter';
import { getPolicyURLBasedOnLegalEntity } from './../../../core/utils/PolicyURLGetterBasedOnLegalEntity/PolicyURLGetterBasedOnLegalEntity';
import { getReferringCompany } from '../../../core/utils/ReferringCompanyValueGetter/ReferringCompanyValueGetter';
import { GoogleAnalyticTags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../../core/utils/GoogleAnalyticTag';
import { GoogleAnalytics4Tags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
import { GetDropdownReferenceDataForMultiselect } from '../../../core/utils/GetDropdownReferenceData';
// Constants
import {
	CHOOSE_PASSWORD,
	CONFIRM_PASSWORD,
	DEFAULT_LANGUAGE,
	MASK_CLASS,
	PARTNER,
	REFERRED_CLIENT_TYPES,
	TORFX,
	TORFX_OZ
} from '../../../core/utils/Constants/Constants';

const REFERENCE_DATA: any = GetDropdownReferenceDataForMultiselect();

const STEP_FIELDS_MAPPING = {
	'firstName': 'firstName',
	'email': 'email',
	'mobile': 'mobile',
	'lastName': 'lastName',
	'companyName': 'companyName',
	'trading_name': 'trading_name',
	'phone': 'phone',
	'best_time_to_call': {
		cookieName: 'est_transaction_value',
		fieldSpecificEventLabel: GoogleAnalyticTags.referAClient.select.contactDetailsBestTimeToCall,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.referAClient.select.contactDetailsBestTimeToCall,
		moduleName: GoogleAnalytics4Tags.referAClient.name,
		valueSelector: { 'timeToCall': '' }
	},
	'est_transaction_value': {
		cookieName: 'est_transaction_value',
		fieldSpecificEventLabel: GoogleAnalyticTags.referAClient.select.transactionDetails.estimatedTransactionValue,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.referAClient.select.transactionDetails.estimatedTransactionValue,
		moduleName: GoogleAnalytics4Tags.referAClient.name,
		valueSelector: { 'transactionValue': '' }
	},
	'etv': {
		cookieName: 'etv',
		fieldSpecificEventLabel: GoogleAnalyticTags.registerClient.select.transactionDetails.estimatedTransactionValue,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.registerClient.select.transactionDetails.estimatedTransactionValue,
		moduleName: GoogleAnalytics4Tags.registerClient.name,
		valueSelector: { 'transactionValue': '' }
	},
	'sellingCurrencyRegClient': {
		cookieName: 'sellingCurrencyRegClient',
		fieldSpecificEventLabel: GoogleAnalyticTags.registerClient.select.transactionDetails.currencyYourClientHas,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.registerClient.select.transactionDetails.currencyYourClientHas,
		moduleName: GoogleAnalytics4Tags.registerClient.name,
		valueSelector: { 'currencyClientHas': '' }
	},
	'buyingCurrencyRegClient': {
		cookieName: 'buyingCurrencyRegClient',
		fieldSpecificEventLabel: GoogleAnalyticTags.registerClient.select.transactionDetails.currencyYourClientWants,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.registerClient.select.transactionDetails.currencyYourClientWants,
		moduleName: GoogleAnalytics4Tags.registerClient.name,
		valueSelector: { 'currencyClientWants': '' }
	},
	'sellingCurrency': {
		cookieName: 'sellingCurrency',
		fieldSpecificEventLabel: GoogleAnalyticTags.referAClient.select.transactionDetails.currencyYourClientHas,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.referAClient.select.transactionDetails.currencyYourClientHas,
		moduleName: GoogleAnalytics4Tags.referAClient.name,
		valueSelector: { 'currencyClientHas': '' }
	},
	'buyingCurrency': {
		cookieName: 'buyingCurrency',
		fieldSpecificEventLabel: GoogleAnalyticTags.referAClient.select.transactionDetails.currencyYourClientWants,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.referAClient.select.transactionDetails.currencyYourClientWants,
		moduleName: GoogleAnalytics4Tags.referAClient.name,
		valueSelector: { 'currencyClientWants': '' }
	},
	'client_type': {
		cookieName: 'client_type',
		fieldSpecificEventLabel: GoogleAnalyticTags.referAClient.select.requirementDetails.affiliateReferredClientTypes,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.referAClient.select.requirementDetails.affiliateReferredClientTypes,
		moduleName: GoogleAnalytics4Tags.referAClient.name,
		valueSelector: { 'affiliate_client_types': '' }
	},
	'businessSector': {
		cookieName: 'businessSector',
		fieldSpecificEventLabel: GoogleAnalyticTags.registration.select.affiliateBusinessSector,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.registration.select.affiliateBusinessSector,
		moduleName: GoogleAnalytics4Tags.registration.name,
		valueSelector: { 'your_business_sector': '' }
	},
	'businessCategory': {
		cookieName: 'businessCategory',
		fieldSpecificEventLabel: GoogleAnalyticTags.registration.select.affiliateBusinessCategory,
		fieldSpecificEventLabelForGA4: GoogleAnalytics4Tags.registration.select.affiliateBusinessCategory,
		moduleName: GoogleAnalytics4Tags.registration.name,
		valueSelector: { 'your_business_category': '' }
	}
};

interface APStepPanelProps extends WithStyles<typeof APStepPanelStyles> {
	definition: any,
	form: any,
	onBlur?: any,
	onChange?: any,
	onClick?: any,
	onGoToStep?: any,
	onValidStateChange: any,
	gotoStep?: any,
	touched: any,
	translate: any,
	sectionLabelError?: string,
	doesCustomerAlreadyExists?: boolean,
	customerAlreadyExistsCheckInProgress?: boolean,
	legalEntity?: string,
	addressCategoryOfUSAState?: string,
	jointAddressCategoryOfUSAState?: string,
	addressCountry?: string,
	jointAddressCountry?: string,
	dropdownOption?: EtvOptions,
	flags?: LDFlagSet
}

class APStepPanel extends Component<APStepPanelProps, {}> {

	componentDidMount() {
		this.validate();
	}

	componentDidUpdate(prevProps: APStepPanelProps) {
		const shouldValidate = (
			!isEqual(prevProps.form, this.props.form) ||
			!isEqual(prevProps.definition, this.props.definition) ||
			!isEqual(prevProps.customerAlreadyExistsCheckInProgress, this.props.customerAlreadyExistsCheckInProgress) ||
			!isEqual(prevProps.doesCustomerAlreadyExists, this.props.doesCustomerAlreadyExists)
		);

		if (shouldValidate) {
			this.validate();
		}
	}

	evaluate(block: any) {
		const {
			form,
			flags = {}
		} = this.props;

		if (block.condition) {
			if (!block.condition.value.includes(form[block.condition.name])) {
				return false;
			}
		}

		if (block.flagCondition) {
			const shouldRemoveBlock: boolean = flags[block.flagCondition.flagName] ?? true;

			if (!shouldRemoveBlock) {
				return false;
			}
		}

		return true;
	}

	getLabelByValue(value: string, optionList: dropdownOptionType[]) {
		const option: dropdownOptionType | undefined = optionList.find(option => option.value === value);
		return option ? option.label : null;
	}

	getSummaryValue(summary: any) {
		const { translate } = this.props;

		const userSelectedLanguage: string = localStorage.getItem('preferredLanguage') ?? DEFAULT_LANGUAGE;

		const {
			combine,
			format,
			using = ' '
		} = summary;

		let text = combine.map((field: any) => {
			let value: any;

			switch (field) {
				case 'salutation': {
					const salutationValue: string = get(this.props.form, field);
					const dropdownOptionsForSalutation: { options: dropdownOptionType[] } = salutations[userSelectedLanguage];
					const salutationLabel: string | null = this.getLabelByValue(salutationValue, dropdownOptionsForSalutation.options);

					value = salutationLabel ?? '';
					break;
				}
				case 'personalClients': {
					const isPersonalClientsChecked: boolean = get(this.props.form, field);
					value = isPersonalClientsChecked ? translate('become.partner.form.personalClients.label') : '';
					break;
				}
				case 'onlineMarketSellers': {
					const isOnlineMarketSellersChecked: boolean = get(this.props.form, field);
					value = isOnlineMarketSellersChecked ? translate('settings.client.onlinemarketplace.label') : '';
					break;
				}
				case 'corporateClients': {
					const isCorporateClientsChecked: boolean = get(this.props.form, field);
					value = isCorporateClientsChecked ? translate('become.partner.form.corporateClients.label') : '';
					break;
				}
				case 'businessSector': {
					const businessSectorValue: string = get(this.props.form, field);
					const dropdownOptionsForBusinessSector: { options: dropdownOptionType[] } = businessSector[userSelectedLanguage];
					const businessSectorLabel: string | null = this.getLabelByValue(businessSectorValue, dropdownOptionsForBusinessSector.options);

					value = businessSectorLabel ?? '';
					break;
				}
				case 'businessCategory': {
					const businessCategoryValue: string = get(this.props.form, field);
					const dropdownOptionsForBusiness: { options: dropdownOptionType[] } = businessCategory[userSelectedLanguage];
					const businessCategoryLabel: string | null = this.getLabelByValue(businessCategoryValue, dropdownOptionsForBusiness.options);

					value = businessCategoryLabel ?? '';
					break;
				}
				case 'taxNumber': {
					const taxNumberValue = get(this.props.form, field);
					value = taxNumberValue.toUpperCase();
					break;
				}
				default: {
					value = get(this.props.form, field);
					break;
				}
			}

			if (value && value.hasOwnProperty('select')) {
				return `${value.select}${value.input ? value.input : ''}`;
			}

			if (value && (field === 'country' || field === 'industry' || field === 'language')) {
				const refData = REFERENCE_DATA[field].options;
				let valText: string = '';
				if (value === 'USA' && field === 'country') {
					valText = "United States of America ";
				} else {
					refData.forEach((item: any) => {
						if (value.includes(item.value)) {
							valText += item.label.trim() + ', ';
						}
					});
				}
				return valText.trim().slice(0, -1);
			}

			if (value &&
				(field === 'account.payment_country' ||
					field === 'address.country' ||
					field === 'jointAddress.country')) {
				return addressCountryLabelMapping(value);
			}

			return value;

		}).filter((value: any) => !!value).join(using);

		if (format === 'date') {
			text = moment(text).format('DD/MM/YYYY');
		}

		return text ? text : '—';
	}

	isFieldValid(field: any, form: any) {
		let value: any = form[field.name];
		const FIELD_WITHOUT_GA_TAGS = ['firstName', 'lastName', 'companyName', 'trading_name', 'email', 'taxNumber'];
		const CURRENT_FIELD = STEP_FIELDS_MAPPING[field.name];
		const CURRENT_FIELD_COOKIE_NAME = CURRENT_FIELD?.cookieName;
		const CURRENT_FIELD_EVENT_LABEL = CURRENT_FIELD?.fieldSpecificEventLabel;
		const CURRENT_FIELD_EVENT_LABEL_GA4 = CURRENT_FIELD?.fieldSpecificEventLabelForGA4;
		const CURRENT_FIELD_MODULE_NAME_GA4 = CURRENT_FIELD?.moduleName;
		const CURRENT_FIELD_EVENT_LABEL_VALUE_SELECTOR = CURRENT_FIELD?.valueSelector ? CURRENT_FIELD.valueSelector : '';
		const isFieldWithoutGATags = FIELD_WITHOUT_GA_TAGS.includes(CURRENT_FIELD);
		const FIELDS_ACCEPTING_AFTER_SPACE = ['firstName', 'firstNames', 'lastName', 'companyName', 'trading_name'];

		if (value && isFieldWithoutGATags) {
			value = FIELDS_ACCEPTING_AFTER_SPACE.includes(field.name) ? value.trimStart() : value.trim();
			form[field.name] = value;
		}
		if (value && CURRENT_FIELD_EVENT_LABEL && CURRENT_FIELD_COOKIE_NAME && !isFieldWithoutGATags) {
			if (Cookies.get(CURRENT_FIELD_COOKIE_NAME) === undefined || Cookies.get(CURRENT_FIELD_COOKIE_NAME) === "" ||
				Cookies.get(CURRENT_FIELD_COOKIE_NAME) !== form[field.name]) {
				const key: string = Object.keys(CURRENT_FIELD_EVENT_LABEL_VALUE_SELECTOR)[0];
				const currentValue = form.customerType === PARTNER && field.name === 'client_type' ? REFERRED_CLIENT_TYPES[value] : value;

				CURRENT_FIELD_EVENT_LABEL_VALUE_SELECTOR[key] = currentValue;

				let VALUE_SELECTOR_OBJECT = {};

				VALUE_SELECTOR_OBJECT[key] = CURRENT_FIELD_EVENT_LABEL_VALUE_SELECTOR[key];

				TriggerGoogleAnalyticsTag(
					GoogleAnalyticTags.eventAction.select,
					CURRENT_FIELD_EVENT_LABEL,
					VALUE_SELECTOR_OBJECT
				);
				TriggerGoogleAnalytics4Tag(
					CURRENT_FIELD_MODULE_NAME_GA4,
					GoogleAnalytics4Tags.eventAction.select,
					CURRENT_FIELD_EVENT_LABEL_GA4,
					VALUE_SELECTOR_OBJECT
				);
				Cookies.set(CURRENT_FIELD_COOKIE_NAME, form[field.name]);
			}
		}
		if (STEP_FIELDS_MAPPING[field.name] === undefined) {
			if (value && value.select) {
				if (value.input) {
					value = `${value.select}${value.input}`;
				} else {
					value = '';
				}
			}
		}


		let isValid = true;
		if (!value && (field.isJoint ? form.isJoint : field.required)) {
			isValid = false;
		}

		const { addressCountry } = this.props;
		if (isValid && value && field.validation && (field.isJoint ? form.isJoint : true) &&
			((field.name === 'phone' ? !value : true) || (field.name === 'phone' ? (value?.input !== '' || value?.select !== '') : true))) {


			switch (field.name) {
				case 'mobile':
				case 'phone':
					const { input, select } = (field.name === 'phone') ? this.props.form.phone : this.props.form.mobile;
					const validSelect = Boolean(String(select).match(/^((\+){1}([ 0-9]{1,3}))$/i));
					const validInput = Boolean(String(input).match(/^([0-9]{8,14})$/i));
					isValid = (validSelect && validInput) ? true : false;
					const trimmedValue = value.input.trim();
					const finalValue = {
						displayName: value.displayName,
						input: trimmedValue,
						select: value.select
					}
					form[field.name] = finalValue;
					break;

				case 'email':
					if (field.doesCustomerAlreadyExistsCheck) {
						isValid = Boolean(String(value).match(field.validation)) &&
							(this.props.customerAlreadyExistsCheckInProgress ? false : !this.props.doesCustomerAlreadyExists);
					} else {
						isValid = Boolean(String(value).match(field.validation));
					}
					break;
				case 'jointEmail':
					if (field.doesCustomerAlreadyExistsCheck) {
						isValid = Boolean(String(value).match(field.validation)) &&
							(this.props.customerAlreadyExistsCheckInProgress ? false : !this.props.doesCustomerAlreadyExists) &&
							(value !== form['email']);
					} else {
						isValid = Boolean(String(value).match(field.validation)) && (value !== form['email']);
					}
					break;
				case 'taxNumber':
					const hasTaxField = addressCountry === 'PRT' || addressCountry === 'ESP';
					if (hasTaxField && field.name === 'taxNumber') {
						isValid = Boolean(String(value).match(field.validation));
					}
					break;
				default:
					isValid = Boolean(String(value).match(field.validation));
			}
		}

		return isValid;
	}

	validate() {
		const {
			definition,
			form,
			onValidStateChange,
			addressCountry
		} = this.props;

		let isValid: boolean = true;
		definition.sections.forEach((section: any) => {
			if (!this.evaluate(section) || !section.fields) {
				return;
			}

			section.fields.forEach((field: any) => {
				if (isValid) {
					if (!this.evaluate(field)) {
						return;
					}
					const checkTaxNumberValidity = (addressCountry === 'PRT' || addressCountry === 'ESP');
					if (field.name === 'taxNumber' && !checkTaxNumberValidity) {
						return null;
					}
					isValid = this.isFieldValid(field, form);

					if ((field.name === 'sellingCurrency' ||
						field.name === 'buyingCurrency') &&
						(form.sellingCurrency === form.buyingCurrency)) {
						isValid = false;
					}

					if ((field.name === 'sellingCurrencyRegClient' ||
						field.name === 'buyingCurrencyRegClient') &&
						(form.sellingCurrencyRegClient === form.buyingCurrencyRegClient)) {
						isValid = false;
					}
					if ((field.name === CHOOSE_PASSWORD ||
						field.name === CONFIRM_PASSWORD) &&
						(form.choosePassword !== form.confirmPassword)) {
						isValid = false;
					}
				}
			});
		});

		onValidStateChange(isValid);
	}

	checkProperties(obj) {
		for (var key in obj) {
			if (obj[key] !== null && obj[key] !== "")
				return false;
		}
		return true;
	}

	renderItems(accordion: any, classes: any, translate: any) {
		return accordion.items.map((item: any) => {
			const value = this.getSummaryValue(item);
			let toBeCapitalised: boolean = false;
			switch (item.key) {
				case 'become.partner.summary.languages':
				case 'become.partner.summary.industries':
					toBeCapitalised = true;

					break;
			}

			if (!this.evaluate(item)) return null;
			if (!value || value === '—') return null;

			return (
				<div key={item.key} className={classes.summaryItem}>
					<span className={classes.summaryItemLabel}>
						{translate(item.key)}
					</span>
					<span className={classes.summaryItemValue + (toBeCapitalised ? ' capitalise' : '')}>
						{translate(item.key) === 'Password' ? '**********' : value}
					</span>
				</div>
			)
		})
	}

	render() {
		const {
			classes,
			definition,
			form,
			onBlur,
			onChange,
			onClick,
			onGoToStep,
			translate,
			touched,
			legalEntity,
			sectionLabelError,
			addressCategoryOfUSAState,
			jointAddressCategoryOfUSAState,
			addressCountry,
			jointAddressCountry,
			dropdownOption
		} = this.props;

		const doesCustomerAlreadyExists = this.props?.doesCustomerAlreadyExists;
		const customerAlreadyExistsCheckInProgress = this.props?.customerAlreadyExistsCheckInProgress;

		// Get referring company once to avoid multiple cookie reads
		const referringCompany = getReferringCompany();
		const organizationName = getOrganizationName(referringCompany);

		return (
			<div className={classes.panelContainer}>

				{definition.sections.map((section: any, index) => {
					if (!this.evaluate(section)) {
						return null;
					}

					return (
						<div key={`formsection-${index}`} className={classes.formSection}>

							{section.label && (section.isJoint ? form.isJoint : true) && (
								<div className={`radioGroup ${classes.formSectionHeader}`}>
									<APHeading className={classes.formSectionHeading} variant="body1">
										{translate(section.label)}
									</APHeading>

									{sectionLabelError &&
										<APHeading className={classes.panelInlineError} variant="body1">
											{sectionLabelError}
										</APHeading>
									}

									{section.switch && (
										<>
											<RadioGroup
												className={classes.formSectionRadio}
												onChange={onChange}
												name={section.switch.name}
												value={form.customerType}>
												{section.switch.options.map((option: any) => (
													<FormControlLabel
														value={option.value}
														control={<APRadio />}
														label={translate(`${option.key}.label`)}
													/>
												))}
											</RadioGroup>
											{section.enterDetailsCopy && section.enterDetailsCopy.map((enterDetailsText: any) => {
												if (!this.evaluate(enterDetailsText)) {
													return null;
												}
												return (
													<span className={classes.formLabel}>
														{translate(enterDetailsText.detailText)}
													</span>)
											})}
										</>
									)}
								</div>
							)}

							{section.type === 'input' && (
								<div className={classes.formSectionContent}>
									{section.fields.map((field: any) => {
										if (!this.evaluate(field)) {
											return null;
										}
										const hasTaxField = addressCountry === 'PRT' || addressCountry === 'ESP';
										if (field.name === 'taxNumber' && !hasTaxField) {
											return null;
										}
										let ComponentType: any = COMPONENT_DEFINITIONS[field.component];
										if (!ComponentType) {
											ComponentType = COMPONENT_DEFINITIONS.default;
										}

										let error = field.doesCustomerAlreadyExistsCheck ?
											((touched[field.name] && !this.isFieldValid(field, form)) || doesCustomerAlreadyExists) :
											(touched[field.name] && !this.isFieldValid(field, form));

										// Terms and conditions text
										let label
										if (field.name === 'dataTerms') {
											label = (
												<>
													<APLabel className={classes.policyContainerLabel}> {translate('register.customer.summary.dataterms.label1', {
														orgCode: organizationName
													})} </APLabel>
													<div className="policyContainer">
														<ul>
															<li>{translate('register.customer.summary.dataterms.label2')}
																<ul>
																	<li>{translate('register.customer.summary.dataterms.label3')}
																		<a href={getPolicyURLBasedOnLegalEntity(legalEntity)} target="_blank" rel="noopener noreferrer">
																			{translate('register.customer.summary.dataterms.label4',
																				{ orgCode: organizationName })}
																		</a>
																		{translate('register.customer.summary.dataterms.label5')}
																	</li>

																	<li>{translate('register.customer.summary.dataterms.label6')}</li>

																	<li>{translate('register.customer.summary.dataterms.label7',
																		{ orgCode: organizationName })}
																	</li>

																	{(organizationName === TORFX || organizationName === TORFX_OZ) && (
																		<li>{translate('register.customer.summary.dataterms.label10')}</li>
																	)}
																</ul>
															</li>
															<li>{translate('register.customer.summary.dataterms.label8',
																{ orgCode: organizationName })}</li>
														</ul>
														<APLabel>{translate('register.customer.summary.dataterms.label9',
															{
																orgCode_1: organizationName,
																orgCode_2: organizationName,
																orgCode_3: organizationName
															})}
														</APLabel>
													</div>
												</>
											);
										} else if (field.name === 'gdpr_opt_in') {
											label = (
												translate('refer.client.form.gdpr.label', { orgCode: organizationName })
											);
										} else if (field.name === 'is_referral_agreed_to_share_contact_info') {
											label = (
												translate('refer.client.form.share.contact.label', { orgCode: organizationName })
											);
										} else {
											label = (
												translate.has(`${field.key}.label`) &&
												translate(`${field.key}.label`)
											);
										}

										// Route changed to 'register-client' as part of UID-395
										if (window.location.href.indexOf('/register_client') >= 0) {
											if (field.name === 'addressUSAStateDisclaimer' &&
												addressCategoryOfUSAState &&
												addressCategoryOfUSAState !== 'A' &&
												addressCountry === 'USA') {
												return (<div className={classes.stateOfUSADisclaimerWrapper}><APAlertMessage
													className={`innerWrapper`}
													title={translate("address.lookup.category.confirmation.title.label")}
													message={translate(`address.lookup.category.${addressCategoryOfUSAState}.confirmation.message.label`,
														{
															orgCode_1: organizationName,
															orgCode_2: organizationName
														})}
													severity={"info"}
													icon={'info'}
													hideCloseIcon={true} /></div>);

											} else if (field.name === 'jointAddressUSAStateDisclaimer' &&
												jointAddressCategoryOfUSAState &&
												jointAddressCategoryOfUSAState !== 'A' &&
												jointAddressCountry === 'USA') {
												return (<div className={classes.stateOfUSADisclaimerWrapper}><APAlertMessage
													className={`innerWrapper`}
													title={translate("address.lookup.category.confirmation.title.label")}
													message={translate(`address.lookup.category.${jointAddressCategoryOfUSAState}.confirmation.message.label`,
														{
															orgCode_1: organizationName,
															orgCode_2: organizationName
														})}
													severity={"info"}
													icon={'info'}
													hideCloseIcon={true} /></div>);
											}
										}

										// Terms Text
										if (`${field.key}.label` === 'become.partner.summary.terms.label') {

											const termsText = (translate('become.partner.summary.terms.label', {
												company: translate('become.partner.summary.terms.company')
											}));

											label = (<>
												{termsText}
												<a className={classes.checkboxLink}
													href={translate('become.partner.summary.terms.link.url')}
													target="_blank" rel="noopener noreferrer">
													{translate('become.partner.summary.terms.link.text')}
												</a>
											</>);
										}

										//commission Opt out text
										if (`${field.key}.label` === 'refer.client.form.commission.label') {
											label = <>
												<span className={classes.checkboxBoldText}>{translate('refer.client.form.commission.label.bold')}</span>
												{translate('refer.client.form.commission.label')}
											</>;
										}

										let countryCodeLabel = '';
										if (field.name === 'phone' || field.name === 'mobile' || field.name === 'jointMobile') {
											countryCodeLabel = translate(`${field.key}.code.label`)
										}

										let helper = translate.has(`${field.key}.helper`) && translate(`${field.key}.helper`);
										if (error && translate.has(`${field.key}.error`)) {
											helper = translate(`${field.key}.error`);
										}

										if (field.doesCustomerAlreadyExistsCheck) {
											customerAlreadyExistsCheckInProgress && (helper = translate(`${field.key}.exists.checking`));
											doesCustomerAlreadyExists && (helper = translate(`${field.key}.exists.error`));
										}

										if (field.name === 'buyingCurrency' &&
											form.sellingCurrency &&
											form.buyingCurrency &&
											form.sellingCurrency === form.buyingCurrency) {
											error = true;
											helper = translate(`refer.client.summary.buyingcurrency.error`);
										}
										if (field.name === 'buyingCurrencyRegClient' &&
											form.sellingCurrencyRegClient &&
											form.buyingCurrencyRegClient &&
											form.sellingCurrencyRegClient === form.buyingCurrencyRegClient) {
											error = true;
											helper = translate(`refer.client.summary.buyingcurrency.error`);
										}

										if (field.name === CONFIRM_PASSWORD &&
											form.choosePassword &&
											form.confirmPassword &&
											form.choosePassword !== form.confirmPassword) {
											error = true;
											helper = translate(`invite.affiliate.form.password.match.error`);
										}

										return (section.isJoint ? form.isJoint : true) && (
											<ComponentType
												key={field.key}
												className={classes.formSectionInput}
												error={error}
												helperText={helper}
												label={label}
												countryCodeLabel={countryCodeLabel}
												onBlur={onBlur}
												onChange={onChange}
												name={field.name}
												value={form[field.name]}
												type={field.reference}
												passwordCriteria={field.passwordCriteria}
												selectedValues={form[field.name]}
												isJoint={field.isJoint}
												isRegistration={field.isRegistration}
												diabledAutofill={(field.disableautofill) ? field.disableautofill : "on"}
												dropdownOption={dropdownOption}
												supportsUnmasking={field.supportsUnmasking}
												isCoralogixMasked={field.isCoralogixMasked}
											/>
										);
									})}
								</div>
							)}

							{section.link && (section.isJoint ? form.isJoint : true) && <div className={classes.linkButton} id={'link'} onClick={onClick}>{translate(section.link)}</div>}

							{section.linkDescription && form.showLinkDescription && <div className={classes.referenceDetails}>{translate(section.linkDescription)}</div>}

							{section.referenceDetails && <div className={classes.referenceDetails}>{translate(section.referenceDetails)}</div>}

							{section.type === 'summary' && (section.isJoint ? form.isJoint : true) &&
								(
									<div className={classes.summaryContent}>
										{section.summaries.map((summary: any) => {
											if (!this.evaluate(summary)) {
												return null;
											}

											const value = this.getSummaryValue(summary);
											let toBeCapitalised = summary.key === 'become.partner.summary.address';

											return (
												<div key={summary.key} className={classes.summaryItem}>
													<span className={classes.summaryItemLabel}>
														{translate(summary.key)}
													</span>
													<span className={classnames(classes.summaryItemValue + (toBeCapitalised ? ' capitalise' : ''), { [MASK_CLASS]: summary?.isCoralogixMasked })}>
														{value && value.length > 0 ? value : ''}
													</span>
												</div>
											)
										})}
									</div>
								)}

							{section.type === 'accordions' && (
								<div className={`stepAccordionGroup`}>
									{section.accordions.map((accordion: any, key) => {
										if (!this.evaluate(accordion)) {
											return null;
										}
										const accordionItems = this.renderItems(accordion, classes, translate);
										const isEmpty = this.checkProperties(accordionItems);
										return (
											<Accordion className={`stepAccordion`} key={`accordion-register-${key}`} defaultExpanded={key === 0}>
												<AccordionSummary expandIcon={<AccordionIcon />} aria-controls={`accordion-register-${key}`} id={`accordion-register-${key}`}>
													<Typography>{translate(accordion.label)}</Typography>
												</AccordionSummary>
												<AccordionDetails>
													{isEmpty ? (<p className={classes.summaryItemValueNone}>{translate('become.partner.summary.no.details.text')}</p>) : accordionItems}
													<APButton onClick={onGoToStep.bind(this, key)} className={classes.summaryEditBtn} variant="outlined">{isEmpty ? translate(`step.panel.form.button.add.details`) : translate(`step.panel.form.button.edit.details`)}</APButton>
												</AccordionDetails>
											</Accordion>
										)
									})}
								</div>
							)}

						</div>
					)
				})}
			</div>
		)
	}
}

export default withFlags(
	withTranslate(
		withStyles(APStepPanelStyles)(APStepPanel)
	)
);
